var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import { Grid, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Input from './Input';
import Button from '../Button';
import Select from '../Select';
import PermsissionsTabs from './PermsissionsTabs';
import { Mode } from '..';

var useStyles = makeStyles(function (theme) {
  return {
    paper: {
      backgroundColor: theme.palette.background.paper,
      padding: theme.spacing(2)
    },
    label: {
      color: theme.palette.primary.main
    },
    textarea: {
      width: '100%',
      padding: theme.spacing(1)
    },
    submitButton: {
      margin: theme.spacing(1)
    },
    contentLabel: {
      fontSize: '14px'
    }
  };
});

var UserRightForm = function UserRightForm(_ref) {
  var labels = _ref.labels,
      artefactTypes = _ref.artefactTypes,
      dataSpaces = _ref.dataSpaces,
      permissionTabs = _ref.permissionTabs,
      onSubmit = _ref.onSubmit,
      onCancel = _ref.onCancel,
      user = _ref.user,
      artefactID = _ref.artefactID,
      artefactType = _ref.artefactType,
      artefactVersion = _ref.artefactVersion,
      maintenanceAgencyID = _ref.maintenanceAgencyID,
      dataspace = _ref.dataspace,
      mode = _ref.mode,
      modes = _ref.modes,
      onChange = _ref.onChange;

  var classes = useStyles();

  var _useState = useState(R.propOr('*', 'value')(user)),
      userValue = _useState[0],
      setUserValue = _useState[1];

  var _useState2 = useState(R.propOr('*', 'value')(dataspace)),
      dataspaceValue = _useState2[0],
      setDataspaceValue = _useState2[1];

  var _useState3 = useState(R.propOr('*', 'value')(artefactType)),
      artefactTypeValue = _useState3[0],
      setArtefactTypeValue = _useState3[1];

  var _useState4 = useState(R.propOr('*', 'value')(maintenanceAgencyID)),
      maintenanceAgencyIDValue = _useState4[0],
      setMaintenanceAgencyIDValue = _useState4[1];

  var _useState5 = useState(R.propOr('*', 'value')(artefactID)),
      artefactIDValue = _useState5[0],
      setArtefactIDValue = _useState5[1];

  var _useState6 = useState(R.propOr('*', 'value')(artefactVersion)),
      artefactVersionValue = _useState6[0],
      setArtefactVersionValue = _useState6[1];

  var initialSelectedPermissions = R.pipe(R.pathOr([], ['options', 'data']), R.filter(R.prop('isSelected')), R.reduce(function (acc, _ref2) {
    var id = _ref2.id;
    return R.assoc(id, id, acc);
  }, {}))(permissionTabs);

  var _useState7 = useState(initialSelectedPermissions),
      selectedPermissions = _useState7[0],
      setSelectedPermissions = _useState7[1];

  var onCheckPermission = function onCheckPermission(id) {
    if (R.has(id, selectedPermissions)) {
      setSelectedPermissions(R.dissoc(id, selectedPermissions));
    } else {
      setSelectedPermissions(R.assoc(id, id, selectedPermissions));
    }
  };
  var permissionsIdsIndexedByGroups = R.reduce(function (acc, group) {
    return R.assoc(group.id, group.options, acc);
  }, {}, R.pathOr([], ['permissions', 'data'], permissionTabs));
  var selectedPermissionsGroups = R.pipe(R.pathOr([], ['permissions', 'data']), R.filter(function (group) {
    return group.options && R.all(function (id) {
      return R.has(id, selectedPermissions);
    }, group.options || []);
  }), R.reduce(function (acc, _ref3) {
    var id = _ref3.id;
    return R.assoc(id, id, acc);
  }, {}))(permissionTabs);

  var onCheckPermissionGroup = function onCheckPermissionGroup(id) {
    if (R.has(id, selectedPermissionsGroups)) {
      setSelectedPermissions(R.omit(permissionsIdsIndexedByGroups[id], selectedPermissions));
    } else {
      setSelectedPermissions(_extends({}, selectedPermissions, R.indexBy(R.identity, permissionsIdsIndexedByGroups[id])));
    }
  };
  var evolvedPermissions = R.pipe(R.over(R.lensPath(['options', 'data']), R.map(function (opt) {
    return R.pipe(R.assoc('isSelected', R.has(opt.id, selectedPermissions)), R.assoc('disabled', !R.is(Function, onSubmit)))(opt);
  })), R.over(R.lensPath(['permissions', 'data']), R.map(function (opt) {
    return R.pipe(R.assoc('isSelected', R.has(opt.id, selectedPermissionsGroups)), R.assoc('disabled', !R.is(Function, onSubmit)))(opt);
  })))(permissionTabs);

  var onChangeDataspace = function onChangeDataspace(value) {
    return setDataspaceValue(value);
  };
  var onChangeArtefactType = function onChangeArtefactType(value) {
    return setArtefactTypeValue(value);
  };

  var handleCancel = function handleCancel() {
    if (R.is(Function)(onCancel)) return onCancel();
  };
  var handleSubmit = function handleSubmit() {
    if (R.is(Function)(onSubmit)) return onSubmit({
      user: userValue,
      dataspace: dataspaceValue,
      artefactType: artefactTypeValue,
      maintenanceAgencyID: maintenanceAgencyIDValue,
      artefactID: artefactIDValue,
      artefactVersion: artefactVersionValue,
      checkedPermissions: R.values(selectedPermissions),
      isGroup: R.equals(mode, 'group')
    });
  };

  var isErrorDataspace = R.pipe(R.find(R.propEq('value', dataspaceValue)), R.isNil)(dataSpaces);
  var isErrorArtefactType = R.pipe(R.find(R.propEq('value', artefactTypeValue)), R.isNil)(artefactTypes);
  var isValidPermissions = !R.isEmpty(selectedPermissions);
  var isValidFields = R.all(function (value) {
    return !R.isEmpty(value) && !R.isNil(value);
  }, [userValue, maintenanceAgencyIDValue, artefactIDValue, artefactVersionValue, dataspaceValue, artefactTypeValue]);

  var hasPermissionsSelectionChanged = !R.equals(selectedPermissions, initialSelectedPermissions);
  var hasFieldsChanged = !R.all(function (_ref4) {
    var state = _ref4[0],
        initial = _ref4[1];
    return R.equals(state, initial);
  }, [[userValue, R.propOr('', 'value', user)], [maintenanceAgencyIDValue, R.propOr('', 'value', maintenanceAgencyID)], [artefactIDValue, R.propOr('', 'value', artefactID)], [artefactVersionValue, R.propOr('', 'value', artefactVersion)], [dataspaceValue, R.propOr('', 'value', dataspace)], [artefactTypeValue, R.propOr('', 'value', artefactType)]]);
  var isValid = isValidPermissions && isValidFields && (hasPermissionsSelectionChanged || hasFieldsChanged);

  return React.createElement(
    Grid,
    { container: true, className: classes.paper, 'data-testid': 'permission-test-id' },
    React.createElement(
      Grid,
      { item: true, container: true, xs: 12, md: 12, justifyContent: 'space-between' },
      React.createElement(
        Grid,
        { item: true, container: true, xs: 12, sm: 12, md: 6, 'flex-direction': 'column' },
        React.createElement(
          Grid,
          { item: true, container: true, xs: 12, alignItems: 'center' },
          React.createElement(
            Grid,
            { item: true, xs: 12, md: 5, style: { marginTop: '4px' } },
            React.createElement(Mode, { modes: modes, mode: mode, changeMode: onChange, isSmall: true })
          ),
          React.createElement(
            Grid,
            { item: true, xs: 12, md: 7 },
            React.createElement(Input, {
              variant: 'outlined',
              name: 'User',
              type: 'text',
              fullWidth: true,
              value: userValue,
              onChange: function onChange(e) {
                return setUserValue(e.target.value);
              },
              isControlled: true,
              textFieldProps: { disabled: R.propOr(false, 'disabled')(user) }
            })
          )
        ),
        React.createElement(
          Grid,
          { item: true, container: true, xs: 12, alignItems: 'center' },
          React.createElement(
            Grid,
            { item: true, xs: 12, md: 5 },
            React.createElement(
              Typography,
              { variant: 'h6', className: classes.contentLabel, style: { paddingTop: 8 } },
              R.prop('dataspace')(labels)
            )
          ),
          React.createElement(
            Grid,
            { item: true, xs: 12, md: 7 },
            React.createElement(Select, {
              value: R.defaultTo('*', dataspaceValue),
              items: dataSpaces,
              onChange: onChangeDataspace,
              textFieldProps: {
                disabled: R.propOr(false, 'disabled')(dataspace),
                error: isErrorDataspace
              }
            })
          )
        ),
        React.createElement(
          Grid,
          { item: true, container: true, xs: 12, alignItems: 'center' },
          React.createElement(
            Grid,
            { item: true, xs: 12, md: 5 },
            React.createElement(
              Typography,
              { variant: 'h6', className: classes.contentLabel, style: { paddingTop: 8 } },
              R.prop('artefactType')(labels)
            )
          ),
          React.createElement(
            Grid,
            { item: true, xs: 12, md: 7 },
            React.createElement(Select, {
              value: R.defaultTo('*', artefactTypeValue),
              items: artefactTypes,
              onChange: onChangeArtefactType,
              textFieldProps: {
                disabled: R.propOr(false, 'disabled')(artefactType),
                error: isErrorArtefactType
              }
            })
          )
        )
      ),
      React.createElement(
        Grid,
        { item: true, container: true, xs: 12, sm: 12, md: 5, 'flex-direction': 'column' },
        React.createElement(
          Grid,
          { item: true, container: true, xs: 12, alignItems: 'center' },
          React.createElement(
            Grid,
            { item: true, xs: 12, md: 4 },
            React.createElement(
              Typography,
              { variant: 'h6', className: classes.contentLabel, style: { paddingTop: 8 } },
              R.prop('maintenanceAgencyID')(labels)
            )
          ),
          React.createElement(
            Grid,
            { item: true, xs: 12, md: 8 },
            React.createElement(Input, {
              variant: 'outlined',
              name: 'title',
              type: 'text',
              fullWidth: true,
              value: maintenanceAgencyIDValue,
              onChange: function onChange(e) {
                return setMaintenanceAgencyIDValue(e.target.value);
              },
              isControlled: true,
              textFieldProps: {
                disabled: R.propOr(false, 'disabled')(maintenanceAgencyID)
              }
            })
          )
        ),
        React.createElement(
          Grid,
          { item: true, container: true, xs: 12, alignItems: 'center' },
          React.createElement(
            Grid,
            { item: true, xs: 12, md: 4 },
            React.createElement(
              Typography,
              { variant: 'h6', className: classes.contentLabel, style: { paddingTop: 8 } },
              R.prop('artefactID')(labels)
            )
          ),
          React.createElement(
            Grid,
            { item: true, xs: 12, md: 8 },
            React.createElement(Input, {
              variant: 'outlined',
              name: 'title',
              type: 'text',
              fullWidth: true,
              value: artefactIDValue,
              onChange: function onChange(e) {
                return setArtefactIDValue(e.target.value);
              },
              isControlled: true,
              textFieldProps: { disabled: R.propOr(false, 'disabled')(artefactID) }
            })
          )
        ),
        React.createElement(
          Grid,
          { item: true, container: true, xs: 12, alignItems: 'center' },
          React.createElement(
            Grid,
            { item: true, xs: 12, md: 4 },
            React.createElement(
              Typography,
              { variant: 'h6', className: classes.contentLabel, style: { paddingTop: 8 } },
              R.prop('artefactVersion')(labels)
            )
          ),
          React.createElement(
            Grid,
            { item: true, xs: 12, md: 8 },
            React.createElement(Input, {
              variant: 'outlined',
              name: 'title',
              type: 'text',
              fullWidth: true,
              value: artefactVersionValue,
              onChange: function onChange(e) {
                return setArtefactVersionValue(e.target.value);
              },
              isControlled: true,
              textFieldProps: {
                disabled: R.propOr(false, 'disabled')(artefactVersion)
              }
            })
          )
        )
      )
    ),
    React.createElement(
      Grid,
      { item: true, container: true },
      React.createElement(PermsissionsTabs, {
        data: evolvedPermissions,
        labels: labels,
        onCheckPermission: onCheckPermission,
        onCheckPermissionGroup: onCheckPermissionGroup
      })
    ),
    React.createElement(
      Grid,
      { item: true, container: true, justifyContent: 'flex-end', alignItems: 'center' },
      React.createElement(
        Grid,
        { item: true },
        React.createElement(
          Button,
          {
            'aria-label': R.prop('cancel')(labels),
            type: 'submit',
            disabled: false,
            variant: 'contained',
            alternative: 'siscc',
            onClick: handleCancel,
            size: 'medium',
            className: classes.submitButton
          },
          R.prop('cancel')(labels)
        )
      ),
      R.is(Function, onSubmit) && React.createElement(
        Grid,
        { item: true },
        React.createElement(
          Button,
          {
            'aria-label': R.prop('submit')(labels),
            type: 'submit',
            disabled: !isValid || isErrorArtefactType || isErrorDataspace,
            variant: 'contained',
            color: 'primary',
            alternative: 'siscc',
            onClick: handleSubmit,
            size: 'medium',
            className: classes.submitButton
          },
          R.prop('submit')(labels)
        )
      )
    )
  );
};
UserRightForm.propTypes = process.env.NODE_ENV !== "production" ? {
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  artefactTypes: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string
  })),
  dataSpaces: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string
  })),
  permissionTabs: PropTypes.object,
  user: PropTypes.shape({
    value: PropTypes.string,
    disabled: PropTypes.bool
  }),
  dataspace: PropTypes.shape({
    value: PropTypes.string,
    disabled: PropTypes.bool
  }),
  artefactType: PropTypes.shape({
    value: PropTypes.string,
    disabled: PropTypes.bool
  }),
  maintenanceAgencyID: PropTypes.shape({
    value: PropTypes.string,
    disabled: PropTypes.bool
  }),
  artefactID: PropTypes.shape({
    value: PropTypes.string,
    disabled: PropTypes.bool
  }),
  artefactVersion: PropTypes.shape({
    value: PropTypes.string,
    disabled: PropTypes.bool
  }),
  labels: PropTypes.shape({
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    user: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    dataspace: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    artefactType: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    maintenanceAgencyID: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    artefactID: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    artefactVersion: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    group: PropTypes.oneOfType([PropTypes.string, PropTypes.element])
  }),
  mode: PropTypes.string,
  modes: PropTypes.array,
  onChange: PropTypes.func
} : {};
export default UserRightForm;